<template>
  <div class="popup-overlay" @click.self="close">
    <div class="popup-content">
      <button type="button" class="close-btn" @click="close">Close ×</button>

			<div class="main">
			   <div>
				   <form ref="form" class="form" novalidate @submit.prevent="handleSubmit">
					   <h3>Let’s talk about <span>what you want to achieve</span></h3>
					   <p class="form__lead">4 quick questions — takes under a minute</p>

						<!-- Прогресс -->
						<ol class="progress" aria-label="Form progress">
							<li
								v-for="(label, i) in stepLabels"
								:key="label"
								class="progress__item"
								:class="{ 'progress__item--active': i + 1 <= step }"
								:aria-current="i + 1 === step ? 'step' : null"
							>
								<span class="progress__bar"></span>
								<span class="progress__label">{{ i + 1 }} · {{ label }}</span>
							</li>
						</ol>

						<!-- Honeypot: всегда в DOM, вне шагов -->
						<input ref="company" type="text" name="company" style="display:none" tabindex="-1" autocomplete="off">

						<transition name="step-fade" mode="out-in" @after-enter="onStepEnter">
							<!-- STEP 1 -->
							<div v-if="step === 1" key="step-1" class="step">
								<h4 ref="stepTitle" class="step__title" tabindex="-1">What are you planning?</h4>
								<div class="options">
									<OptionCard
										v-for="opt in projectOptions"
										:key="opt.value"
										light
										:title="opt.value"
										:subtitle="opt.hint"
										:icon="opt.icon"
										:selected="form.project === opt.value"
										@select="choose('project', opt.value)"
									/>
								</div>
								<button v-if="form.project" type="button" class="btn-primary step__next" @click="nextStep">Continue</button>
							</div>

							<!-- STEP 2 -->
							<div v-else-if="step === 2" key="step-2" class="step">
								<button type="button" class="btn-back" @click="prevStep">← Back</button>
								<h4 ref="stepTitle" class="step__title" tabindex="-1">What's your budget range?</h4>
								<div class="options">
									<OptionCard
										v-for="opt in budgetOptions"
										:key="opt"
										light
										:title="opt"
										:selected="form.budget === opt"
										@select="choose('budget', opt)"
									/>
								</div>
								<button v-if="form.budget" type="button" class="btn-primary step__next" @click="nextStep">Continue</button>
							</div>

							<!-- STEP 3 -->
							<div v-else-if="step === 3" key="step-3" class="step">
								<button type="button" class="btn-back" @click="prevStep">← Back</button>
								<h4 ref="stepTitle" class="step__title" tabindex="-1">Timeline and location</h4>

								<div class="field">
									<span id="popup-timeline-label" class="field__label">When would you like to start? *</span>
									<div class="options options--compact" role="group" aria-labelledby="popup-timeline-label">
										<OptionCard
											v-for="opt in timelineOptions"
											:key="opt"
											light
											compact
											:title="opt"
											:selected="form.timeline === opt"
											@select="selectTimeline(opt)"
										/>
									</div>
									<div v-if="timelineError" class="field__error">{{ timelineError }}</div>
								</div>

								<div class="field">
									<label for="popup-zip" class="field__label">Project ZIP code *</label>
									<input
										id="popup-zip"
										ref="zip"
										type="text"
										inputmode="numeric"
										autocomplete="postal-code"
										maxlength="5"
										placeholder="28277"
										:value="form.zip"
										:class="{ 'is-invalid': zipError }"
										:aria-invalid="zipError ? 'true' : 'false'"
										@input="handleZipInput"
									>
									<div v-if="zipError" class="field__error">{{ zipError }}</div>
								</div>

								<button type="button" class="btn-primary step__next" @click="nextStep">Continue</button>
							</div>

							<!-- STEP 4 -->
							<div v-else key="step-4" class="step">
								<button type="button" class="btn-back" @click="prevStep">← Back</button>
								<h4 ref="stepTitle" class="step__title" tabindex="-1">How can we reach you?</h4>

								<div class="form__group">
									<div class="field">
										<label for="popup-name" class="field__label">Full Name</label>
										<input id="popup-name" v-model.trim="form.name" type="text" autocomplete="name" maxlength="50">
									</div>

									<div class="field">
										<label for="popup-phone" class="field__label">Phone number *</label>
										<input
											id="popup-phone"
											ref="phone"
											v-model="formattedPhone"
											type="tel"
											inputmode="numeric"
											name="phone"
											autocomplete="tel"
											required
											placeholder="+1 (555) 000-0000"
											:class="{ 'is-invalid': showError }"
											:aria-invalid="showError ? 'true' : 'false'"
											@input="handlePhoneInput"
										>
										<div v-if="showError" class="field__error">
											Please enter a valid 10-digit US phone number.
										</div>
									</div>

									<div class="field">
										<label for="popup-email" class="field__label">Email</label>
										<input
											id="popup-email"
											ref="email"
											v-model.trim="form.email"
											type="email"
											autocomplete="email"
											maxlength="100"
											:class="{ 'is-invalid': emailError }"
											:aria-invalid="emailError ? 'true' : 'false'"
											@input="emailError = ''"
										>
										<div v-if="emailError" class="field__error">{{ emailError }}</div>
									</div>

									<div class="field">
										<label for="popup-job" class="field__label">Project details</label>
										<textarea
											id="popup-job"
											v-model="form.job"
											rows="3"
											autocomplete="off"
											maxlength="300"
											placeholder="Anything else we should know — style, rooms, special requests…"
										></textarea>
									</div>
								</div>

								<div class="form__submit-block">
									<div class="form__submit-button">
										<button type="submit" class="btn-primary" :disabled="isSending">
											{{ isSending ? 'Sending…' : 'Request My Estimate' }}
										</button>
										<div class="text text--note">We'll contact you within one business day.</div>
										<div class="text">By submitting this form you agree to our <router-link to="/privacy-policy">Privacy Policy.</router-link></div>
									</div>
								</div>
							</div>
						</transition>

						<div v-if="sendResult" class="form__send-result" role="alert">{{ sendResult }}</div>
				</form>
			</div>
			<div class="data">
				<div class="data__info">
					<div class="data__contact">
						<h3>Contact</h3>
						<div class="data__mail">
							<a href="mailto:sales@kmremodelingservices.com">
						      sales@kmremodelingservices.com
					      </a>
						</div>
						<div class="data__phone">
							<a href="tel:+19808905255">(980) 890-5255</a>
						</div>
					</div>
				</div>
			</div>
		</div>
    </div>
  </div>
</template>

<script>
import OptionCard from './OptionCard.vue';
import multiStepForm from '@/mixins/multiStepForm';

export default {
  name: 'PopapView',

  components: { OptionCard },

  mixins: [multiStepForm],

  emits: ['close'],

  leadForm: {
    label: 'Popup Form',
    attemptsKey: 'attempts',
    formId: 'form-section',
    formName: 'main_contact_form',
    variant: 'popup',
  },

  methods: {
    close() {
      this.$emit('close')
    },
  },
}
</script>

<style lang="scss" scoped>
.popup-overlay {
  position: fixed;
  z-index: 1006;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: end;
  //align-items: center;
}

.popup-content {
  background: #fff;
  padding: 40px;
  border-radius: 10px 0px 0px 10px;
  position: relative;
  transform: translateY(-20px);
  animation: slideIn 0.3s ease forwards;
  // Фиксированная ширина под сетку карточек + прокрутка, если шаг не влезает по высоте
  width: 560px;
  max-width: 100%;
  max-height: 100%;
  overflow-y: auto;
  box-sizing: border-box;
  @media(max-width:650px){
      border-radius: 0px;
  }
  @media(max-width:525px){
      padding: 30px 16px;
  }
}

@keyframes slideIn {
  from {
    transform: translateY(-20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.close-btn {
  position: absolute;
  right: 10px;
  top: 10px;
  background: transparent;
  border: none;
  font-size: 20px;
  cursor: pointer;
}



.main {
	display: flex;
	flex-direction: column;
	justify-content: center;
	background-color: rgb(255, 255, 255, 1);
	border-radius: 20px;
}

.data {
	display: flex;
   flex-direction: column;
   justify-content: space-between;

		&__info {
			display: flex;
			column-gap: 10px;
			@media (max-width:765px) {
				flex-direction: column;
				row-gap: 0px;
         }
		}

		&__contact {
			display: flex;
			flex-direction: column;
			row-gap: 10px;
			@media(max-width:765px) {
				row-gap: 0px;
			}
			h3 {
	         font-size: 14px;
	         line-height: 1.1;
	         font-weight: 500;
	         color: rgba(25, 25, 25, 0.5);
         }
		}


		&__mail {
			padding-bottom: 0px;
			@media (max-width:765px){
				padding-bottom: 15px;
			}
			a {
	         font-size: 16px;
	         line-height: 1.1;
	         font-weight: 400;
	         color: rgba(25, 25, 25, 1);
				text-decoration: none;
			}
		}


		&__phone {
			a {
	         font-size: 16px;
	         line-height: 1.1;
	         font-weight: 400;
	         color: rgba(25, 25, 25, 1);
				text-decoration: none;
			}
		}
}

@media (max-width:765px) {
	.main {
		flex-direction: column;
		gap: 15px;
	}
}

// ─── FORM ────────────────────────────────────────────────
.form {
	margin-bottom: 32px;

	h3 {
		font-size: 32px;
		font-weight: 500;
		line-height: 1.2;
		color: rgba(25, 25, 25, 1);
		padding-top: 25px;
		margin: 0 0 8px;
		span {
			color: rgba(25, 25, 25, 0.5);
		}

		@media(max-width:768px) {
			font-size: 28px;
		}
		@media(max-width:525px) {
			font-size: 24px;
		}
	}

	&__lead {
		font-size: 14px;
		line-height: 1.5;
		color: rgba(25, 25, 25, 0.5);
		margin: 0 0 24px;
	}

	&__group {
		display: flex;
		flex-direction: column;
		row-gap: 20px;
	}

	&__submit-block {
		margin-top: 32px;
	}

	&__submit-button {
		.text {
			margin-top: 10px;
			font-size: 12px;
			color: rgba(25, 25, 25, 0.4);
			text-align: center;
			a { color: rgba(25, 25, 25, 0.4); }

			&--note {
				font-size: 13px;
				color: rgba(25, 25, 25, 0.6);
			}
		}
	}
}

.form__send-result {
	margin-top: 12px;
	font-size: 14px;
	text-align: center;
	color: #d32f2f;
}

// ─── PROGRESS ────────────────────────────────────────────
.progress {
	list-style: none;
	display: grid;
	grid-template-columns: repeat(4, 1fr);
	gap: 6px;
	padding: 0;
	margin: 0 0 24px;

	&__item {
		display: flex;
		flex-direction: column;
		gap: 8px;
		min-width: 0;
	}

	&__bar {
		height: 4px;
		border-radius: 100px;
		background-color: rgba(25, 25, 25, 0.1);
		transition: background-color 0.3s ease;
	}

	&__label {
		font-size: 12px;
		color: rgba(25, 25, 25, 0.4);
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
		transition: color 0.3s ease;

		@media (max-width: 400px) { font-size: 11px; }
	}

	&__item--active &__bar   { background-color: rgb(248, 171, 0); }
	&__item--active &__label { color: rgba(25, 25, 25, 0.85); }
}

// ─── STEP ────────────────────────────────────────────────
.step {
	display: flex;
	flex-direction: column;

	&__title {
		font-size: 18px;
		font-weight: 500;
		line-height: 1.3;
		color: rgba(25, 25, 25, 1);
		margin: 0 0 16px;

		&:focus { outline: none; }
	}

	&__next { margin-top: 24px; }
}

.options {
	display: grid;
	grid-template-columns: repeat(2, 1fr);
	gap: 12px;

	@media (max-width: 525px) { grid-template-columns: 1fr; }

	&--compact {
		gap: 10px;
		@media (max-width: 525px) { grid-template-columns: repeat(2, 1fr); }
	}
}

.field {
	display: flex;
	flex-direction: column;
	row-gap: 6px;

	& + & { margin-top: 24px; }

	&__label {
		font-size: 16px;
		line-height: 1.4;
		font-weight: 400;
		color: rgba(25, 25, 25, 0.5);
	}

	&__error {
		color: #d32f2f;
		font-size: 12px;
	}

	input,
	textarea {
		padding: 5px;
		font-size: 16px;
		font-family: inherit;
		border: none;
		border-bottom: 0.5px solid rgba(25, 25, 25, 0.3);
		border-radius: 0;
		background-color: transparent;
		color: rgba(25, 25, 25, 1);

		&::placeholder { color: rgba(25, 25, 25, 0.25); }

		&:focus {
			outline: none;
			border-bottom: 1px solid rgba(25, 25, 25, 0.6);
		}

		&.is-invalid { border-bottom: 1px solid #d32f2f; }
	}

	textarea {
		line-height: 1.4;
		resize: none; /* чтобы нельзя было растягивать */

		&::placeholder { font-style: italic; }
	}
}

.form__group .field + .field { margin-top: 0; }

// ─── BUTTONS ─────────────────────────────────────────────
.btn-primary {
	width: 100%;
	padding: 15px 40px;
	background-color: rgba(25, 25, 25, 1);
	color: rgba(255, 255, 255, 1);
	font-family: inherit;
	font-size: 16px;
	font-weight: 400;
	line-height: 1.1;
	border-radius: 100px;
	border: none;
	cursor: pointer;
	transition: opacity 0.2s ease;

	&:hover { opacity: 0.85; }
	&:focus-visible { outline: 2px solid rgb(248, 171, 0); outline-offset: 2px; }
	&:disabled { opacity: 0.6; cursor: default; }

	@media (max-width: 400px) { padding: 14px 16px; }
}

.btn-back {
	align-self: flex-start;
	margin: -6px 0 8px -6px;
	padding: 6px;
	background: none;
	border: none;
	font-family: inherit;
	font-size: 14px;
	color: rgba(25, 25, 25, 0.5);
	cursor: pointer;
	transition: color 0.2s ease;

	&:hover { color: rgba(25, 25, 25, 1); }
	&:focus-visible { outline: 1px solid rgba(25, 25, 25, 0.6); border-radius: 4px; }
}

// ─── STEP TRANSITION ─────────────────────────────────────
.step-fade-enter-active,
.step-fade-leave-active { transition: opacity 0.2s ease; }

.step-fade-enter-from,
.step-fade-leave-to { opacity: 0; }

@media (prefers-reduced-motion: reduce) {
	.step-fade-enter-active,
	.step-fade-leave-active { transition: none; }
}

// ─── PROMO (старый блок, отключён) ──────────────────────
// .promo-selection {
//   margin: 10px 0 20px 0;
//   padding: 15px;
//   background: rgb(0 0 0 / 5%); 
//   border: 1px dashed rgba(216, 158, 28, 0.4);
//   border-radius: 12px;
//   pointer-events: none; // Отключаем клики
//   user-select: none;

//   .promo-label {
//     display: flex;
//     align-items: center;
//     gap: 12px;
//     cursor: default;

//     // Скрываем стандартный системный радио-баттон
//     input[type="radio"] {
//       position: absolute;
//       opacity: 0;
//       width: 0;
//       height: 0;
//       margin: 0;
//     }

//     .radio-visual {
//       width: 20px;
//       height: 20px;
//       border: 2px solid #d89e1c;
//       border-radius: 50%;
//       position: relative;
//       flex-shrink: 0; // ВАЖНО: кружок не сплющится, если текст перенесется

//       &::after {
//         content: '';
//         position: absolute;
//         top: 50%;
//         left: 50%;
//         transform: translate(-50%, -50%);
//         width: 10px;
//         height: 10px;
//         background-color: #d89e1c;
//         border-radius: 50%;
//       }
//     }

//     .promo-text {
//       font-size: 16px;
//       color: #000000;
//       font-weight: 500;
//       line-height: 1.3;
//     }
//   }

//   // --- МОБИЛЬНЫЕ СТИЛИ (Экраны до 790px, как в вашей форме) ---
//   @media (max-width: 790px) {
//     margin: 5px 0 15px 0;
//     padding: 12px;
//     border-radius: 10px;

//     .promo-label {
//       gap: 10px;

//       .radio-visual {
//         width: 18px;
//         height: 18px;

//         &::after {
//           width: 8px;
//           height: 8px;
//         }
//       }

//       .promo-text {
//         font-size: 14px; // Чуть меньше на мобильных, чтобы не загромождать форму
//       }
//     }
//   }

//   // Для очень маленьких экранов (iPhone SE и подобные)
//   @media (max-width: 375px) {
//     .promo-label {
//       .promo-text {
//         font-size: 13px;
//       }
//     }
//   }
// }
</style>
